import React, { useState } from 'react';
import axios from 'axios';
import { Collapse, Button, Spinner } from 'reactstrap';

interface Product {
    purchase_id: number;
    product_title: string;
    amount: number;
    product_type: string;
}

interface TransactionGroup {
    invoice_id: string;
    renewal_number: number;
    transaction_label: string;
    date: string;
    total_amount: number;
    currency: string;
    status: string;
    payment_method_display: string;
    invoice_url?: string;
    invoice_pdf?: string;
    period_start: string;
    period_end: string;
    billing_reason: string;
    stripe_charge_id?: string;
    stripe_payment_intent_id?: string;
    products: Product[];
    product_count: number;
}

interface OrderTransactionHistoryProps {
    orderId: number;
    totalAmount: number;
    productCount: number;
}

const OrderTransactionHistoryExpander: React.FC<OrderTransactionHistoryProps> = ({ 
    orderId,
    totalAmount,
    productCount
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    
    const PREVIEW_LIMIT = 5;  // Show only 5 latest transactions in preview

    const toggleHistory = async () => {
        if (!isOpen && !transactions) {
            // Fetch data when opening for first time
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(
                    `/payments/order/${orderId}/transactions/`,
                    { withCredentials: true }
                );
                
                if (response && response.status === 'SENT') {
                    setTransactions(response);
                } else {
                    setError('Failed to load transaction history');
                }
            } catch (err: any) {
                console.error('Error fetching transaction history:', err);
                setError(err.response?.data?.error || 'Failed to load transactions');
            }
            setLoading(false);
        }
        setIsOpen(!isOpen);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    
    const navigateToBillingHistory = () => {
        window.location.href = '/billing-history';
    };

    return (
        <div className="mt-3 ps-3">
            <Button
                color="link"
                onClick={toggleHistory}
                className="p-0 text-decoration-none d-flex align-items-center gap-2"
                style={{ fontSize: '14px' }}
            >
                <i className={`bx bx-${isOpen ? 'chevron-up' : 'chevron-down'} fs-5`}></i>
                <span className="fw-semibold text-primary">
                    <i className="bx bx-history me-1"></i>
                    Order Billing History ({transactions ? transactions.transaction_groups.length : '...'} transaction{transactions && transactions.transaction_groups.length !== 1 ? 's' : ''})
                </span>
            </Button>

            <Collapse isOpen={isOpen}>
                <div className="mt-3 p-3 rounded-3 bg-light">
                    {loading && (
                        <div className="text-center py-4">
                            <Spinner color="primary" size="sm" /> Loading...
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger mb-0">{error}</div>
                    )}

                    {transactions && !loading && (
                        <>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold text-dark mb-0">Recent Transactions</h6>
                                    {transactions.transaction_groups.length > PREVIEW_LIMIT && (
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={navigateToBillingHistory}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <i className="bx bx-history"></i>
                                            View All ({transactions.transaction_groups.length})
                                        </Button>
                                    )}
                                </div>
                                <div className="d-flex gap-4 mb-3 flex-wrap">
                                    <div className="small">
                                        <span className="text-muted">Lifetime Value:</span>
                                        <span className="fw-bold text-success ms-1">
                                            ${transactions.summary.lifetime_value?.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="small">
                                        <span className="text-muted">Transactions:</span>
                                        <span className="fw-bold ms-1">
                                            {transactions.summary.successful_transactions} successful
                                            {transactions.summary.failed_transactions > 0 && 
                                                `, ${transactions.summary.failed_transactions} failed`
                                            }
                                        </span>
                                    </div>
                                    {transactions.order.next_renewal && (
                                        <div className="small">
                                            <span className="text-muted">Next Billing:</span>
                                            <span className="fw-bold text-primary ms-1">
                                                {formatDate(transactions.order.next_renewal)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0 bg-white">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Transaction</th>
                                            <th>Products</th>
                                            <th>Period</th>
                                            <th>Total Amount</th>
                                            <th>Payment Method</th>
                                            <th>Status</th>
                                            <th>Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.transaction_groups
                                            .slice(0, PREVIEW_LIMIT)  // Show only latest 5 transactions
                                            .map((txn: TransactionGroup, idx: number) => (
                                            <React.Fragment key={txn.invoice_id}>
                                                <tr>
                                                    <td className="small">{formatDate(txn.date)}</td>
                                                    <td>
                                                        <span className={`badge ${txn.renewal_number === 0 ? 'bg-primary' : 'bg-secondary'} px-2 py-1`}>
                                                            {txn.transaction_label}
                                                        </span>
                                                    </td>
                                                    <td className="small">
                                                        {txn.product_count} product{txn.product_count !== 1 ? 's' : ''}
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            {txn.products.slice(0, 2).map(p => p.product_title).join(', ')}
                                                            {txn.products.length > 2 && ` +${txn.products.length - 2} more`}
                                                        </div>
                                                    </td>
                                                    <td className="small text-muted">
                                                        {formatDate(txn.period_start)} - {formatDate(txn.period_end)}
                                                    </td>
                                                    <td className="fw-semibold">${txn.total_amount.toFixed(2)}</td>
                                                    <td className="small">
                                                        <i className="bx bx-credit-card me-1"></i>
                                                        {txn.payment_method_display}
                                                    </td>
                                                    <td>
                                                        {txn.status === 'succeeded' ? (
                                                            <span className="badge bg-success-subtle text-success px-2 py-1">
                                                                <i className="bx bx-check-circle me-1"></i>Paid
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-danger-subtle text-danger px-2 py-1">
                                                                <i className="bx bx-x-circle me-1"></i>Failed
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {txn.invoice_url ? (
                                                            <a 
                                                                href={txn.invoice_url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="btn btn-sm btn-outline-primary"
                                                            >
                                                                <i className="bx bx-download me-1"></i>
                                                                View
                                                            </a>
                                                        ) : (
                                                            <span className="text-muted small">N/A</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {/* Product breakdown row */}
                                                <tr className="table-active">
                                                    <td colSpan={8} style={{ paddingLeft: '40px', fontSize: '12px' }}>
                                                        <strong>Breakdown:</strong>
                                                        {txn.products.map((product, pidx) => (
                                                            <span key={pidx} className="ms-3">
                                                                {product.product_title}: <strong>${product.amount.toFixed(2)}</strong>
                                                                {pidx < txn.products.length - 1 && ' |'}
                                                            </span>
                                                        ))}
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* View All Button at bottom if more than 5 transactions */}
                            {transactions.transaction_groups.length > PREVIEW_LIMIT && (
                                <div className="text-center mt-3">
                                    <Button
                                        color="link"
                                        onClick={navigateToBillingHistory}
                                        className="text-decoration-none"
                                    >
                                        <i className="bx bx-chevron-right me-1"></i>
                                        View All {transactions.transaction_groups.length} Transactions
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Collapse>
        </div>
    );
};

export default OrderTransactionHistoryExpander;

